import React, { useMemo, useState, useCallback, useEffect } from 'react';

const statusBadge = (status) => {
  const s = status || 'offline';
  const color =
    s === 'online' ? 'success' : s === 'idle' ? 'warning' : 'secondary';
  const icon = s === 'online' ? '🟢' : s === 'idle' ? '🟡' : '⚫';
  return (
    <span className={`badge bg-${color}`} style={{ minWidth: 72 }}>
      {icon} {s}
    </span>
  );
};

const headerCols = [
  { key: 'playerId', label: 'Player' },
  { key: 'status', label: 'Status' },
  { key: 'devices', label: 'Devices' },
  { key: 'latencyMs', label: 'Latency' },
  { key: 'sessions', label: 'Sessions' },
  { key: 'lastSeen', label: 'Last Change' },
];

export default function PlayerTable({ rows = [] }) {
  const [sortKey, setSortKey] = useState('status');
  const [sortDir, setSortDir] = useState('asc');
  const [isCompact, setIsCompact] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false,
  );
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    const onResize = () => {
      setIsCompact(window.innerWidth < 768);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onSort = useCallback(
    (key) => {
      if (sortKey === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortKey(key);
        setSortDir('asc');
      }
    },
    [sortKey],
  );

  const sorted = useMemo(() => {
    const cmp = (a, b) => {
      const av = a?.[sortKey];
      const bv = b?.[sortKey];
      if (av === bv) return 0;
      if (sortDir === 'asc') return av > bv ? 1 : -1;
      return av < bv ? 1 : -1;
    };
    return [...rows].sort(cmp);
  }, [rows, sortDir, sortKey]);

  const toggle = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="card shadow-sm w-100">
      <div className="card-body" style={{ minWidth: 0, overflowX: 'auto' }}>
        <h5 className="card-title mb-3">Players</h5>
        {!isCompact && (
          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle">
              <thead>
                <tr>
                  {headerCols.map((c) => (
                    <th
                      key={c.key}
                      style={{ cursor: 'pointer' }}
                      onClick={() => onSort(c.key)}
                    >
                      {c.label}{' '}
                      {sortKey === c.key ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((p) => (
                  <tr key={p.playerId}>
                    <td style={{ fontFamily: 'monospace' }}>{p.playerId}</td>
                    <td>{statusBadge(p.status)}</td>
                    <td>{(p.devices || []).join(', ') || '—'}</td>
                    <td>{p.latencyMs != null ? `${p.latencyMs} ms` : '—'}</td>
                    <td>{p.sessions || p.sessionCount || '—'}</td>
                    <td>
                      {p.lastSeen
                        ? new Date(p.lastSeen).toLocaleTimeString()
                        : '—'}
                    </td>
                  </tr>
                ))}
                {!sorted.length && (
                  <tr>
                    <td
                      colSpan={headerCols.length}
                      className="text-center text-muted"
                    >
                      No players
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        {isCompact && (
          <div className="d-flex flex-column gap-2">
            {sorted.length ? (
              sorted.map((p) => {
                const open = !!expanded[p.playerId];
                return (
                  <div
                    key={p.playerId}
                    className="border rounded p-2 bg-light"
                    style={{ fontSize: '0.95rem' }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <div style={{ fontFamily: 'monospace' }}>
                          {p.playerId}
                        </div>
                        <div className="small text-muted">
                          Latency:{' '}
                          {p.latencyMs != null ? `${p.latencyMs} ms` : '—'}
                        </div>
                      </div>
                      <div className="text-end">
                        {statusBadge(p.status)}
                        <button
                          type="button"
                          className="btn btn-link btn-sm d-block p-0 mt-1"
                          onClick={() => toggle(p.playerId)}
                          aria-expanded={open}
                        >
                          {open ? 'Hide details' : 'Show details'}
                        </button>
                      </div>
                    </div>
                    {open && (
                      <div className="mt-2 small">
                        <div>Devices: {(p.devices || []).join(', ') || '—'}</div>
                        <div>Sessions: {p.sessions || p.sessionCount || '—'}</div>
                        <div>
                          Last change:{' '}
                          {p.lastSeen
                            ? new Date(p.lastSeen).toLocaleTimeString()
                            : '—'}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-center text-muted">No players</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
